import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { X, Copy, Check, QrCode } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
// EPC069-12 (SEPA Credit Transfer) QR-payload — scanbaar door NL/EU-bankapps.
const epcPayload = ({ naam, iban, bedrag, ref }) => [
  'BCD', '002', '1', 'SCT', '', (naam || '').slice(0, 70),
  (iban || '').replace(/\s+/g, '').toUpperCase(),
  `EUR${(Number(bedrag) || 0).toFixed(2)}`, '', '', (ref || '').slice(0, 140), '',
].join('\n');

export default function BetaalQR({ bon, onClose }) {
  const { t } = useT();
  const { gegevens, updateGegevens } = useData();
  const [iban, setIban] = useState(gegevens?.iban || '');
  const [naam, setNaam] = useState(gegevens?.bedrijfsnaam || gegevens?.naam || 'MonteurCentraal');
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState('');
  const bedrag = Math.max(0, Number(bon?.openstaand ?? bon?.totaal ?? 0));
  const ref = `${bon?.nummer || ''}`;

  useEffect(() => {
    const cleaned = (iban || '').replace(/\s+/g, '');
    if (cleaned.length < 15) { setQr(''); return; }
    QRCode.toDataURL(epcPayload({ naam, iban, bedrag, ref }), { margin: 1, width: 320 }).then(setQr).catch(() => setQr(''));
  }, [iban, naam, bedrag, ref]);

  const copy = (txt, key) => { navigator.clipboard?.writeText(txt); setCopied(key); setTimeout(() => setCopied(''), 1500); };
  const saveIban = () => { updateGegevens({ iban: iban.replace(/\s+/g, '').toUpperCase(), bedrijfsnaam: naam }); toast.success(t('Opgeslagen', 'Opgeslagen')); };

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }} data-testid="betaal-qr-modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><QrCode size={18} /> {t('Betaal-QR', 'Betaal-QR')} · {bon?.nummer}</h3>
          <button className="cs-btn ghost" onClick={onClose} data-testid="bq-close"><X size={16} /></button>
        </div>

        <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
          <div><label className="cs-section-title">{t('Tenaamstelling', 'Tenaamstelling')}</label>
            <input className="cs-input" value={naam} onChange={e => setNaam(e.target.value)} data-testid="bq-naam" /></div>
          <div><label className="cs-section-title">IBAN</label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="cs-input" value={iban} onChange={e => setIban(e.target.value)} placeholder="NL00 BANK 0000 0000 00" data-testid="bq-iban" />
              <button className="cs-btn" onClick={saveIban} data-testid="bq-save-iban">{t('Bewaar', 'Bewaar')}</button>
            </div>
          </div>
        </div>

        {qr ? (
          <div style={{ textAlign: 'center' }}>
            <img src={qr} alt="Betaal QR" style={{ width: 200, height: 200 }} data-testid="bq-qr" />
            <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>{t('Scan met je bankapp om', 'Scan met je bankapp om')} <b>{eur(bedrag)}</b> {t('over te maken', 'over te maken')}</div>
          </div>
        ) : (
          <div className="cs-empty" data-testid="bq-no-iban">{t('Vul een geldig IBAN in om de betaal-QR te genereren.', 'Vul een geldig IBAN in om de betaal-QR te genereren.')}</div>
        )}

        {/* Betaalgegevens / betaallink */}
        <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, fontSize: 13 }}>
          {[['IBAN', (iban || '—')], [t('Bedrag', 'Bedrag'), eur(bedrag)], [t('Kenmerk', 'Kenmerk'), ref]].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
              <span style={{ color: '#64748b' }}>{k}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>{v}
                <button onClick={() => copy(String(v), k)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#2563eb', display: 'flex' }} data-testid={`bq-copy-${k}`}>{copied === k ? <Check size={14} /> : <Copy size={14} />}</button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
